import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

export type TaggedSearchSelectOption = {
    id: string | number;
    label: ReactNode;
    header?: boolean;
    icon?: ReactNode;
};

interface Props {
    id: string;
    label?: ReactNode;
    labelClassName?: string;
    className?: string;
    placeholder?: string;
    emptyMessage?: string;
    options: TaggedSearchSelectOption[];
    value: string[];
    onChange: (ids: string[]) => void;
    allValue?: string;
    hideTagIds?: string[];
    inputAddon?: string;
    classNameContainerScroll?: string;
}

const TaggedSearchSelect = ({
    id,
    label,
    labelClassName,
    className,
    placeholder,
    emptyMessage,
    options,
    value,
    onChange,
    allValue,
    hideTagIds,
    inputAddon = 'rioglyph rioglyph-filter',
    classNameContainerScroll,
}: Props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const hiddenTagIds = useMemo(
        () => new Set([...(hideTagIds ?? []), ...(allValue ? [allValue] : [])]),
        [allValue, hideTagIds],
    );

    const selectedTags = useMemo(() => {
        return value
            .filter(selectedId => !hiddenTagIds.has(selectedId))
            .map(selectedId => {
                const option = options.find(item => String(item.id) === selectedId && !item.header);
                return {
                    id: selectedId,
                    label: option?.label ?? selectedId,
                };
            });
    }, [hiddenTagIds, options, value]);

    const filteredOptions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return options;
        }

        const groups: { header: TaggedSearchSelectOption | null; items: TaggedSearchSelectOption[] }[] = [];
        let currentGroup: { header: TaggedSearchSelectOption | null; items: TaggedSearchSelectOption[] } = {
            header: null,
            items: [],
        };

        options.forEach(option => {
            if (allValue && String(option.id) === allValue) {
                return;
            }

            if (option.header) {
                groups.push(currentGroup);
                currentGroup = { header: option, items: [] };
                return;
            }

            currentGroup.items.push(option);
        });
        groups.push(currentGroup);

        const result: TaggedSearchSelectOption[] = [];
        groups.forEach(group => {
            const visibleItems = group.items.filter(item => {
                const text = typeof item.label === 'string' ? item.label : '';
                return text.toLowerCase().includes(term);
            });

            if (!visibleItems.length) {
                return;
            }

            if (group.header) {
                result.push(group.header);
            }

            result.push(...visibleItems);
        });

        return result;
    }, [allValue, options, searchTerm]);

    const toggleOption = (optionId: string) => {
        if (allValue && optionId === allValue) {
            onChange([allValue]);
            return;
        }

        const withoutAll = allValue ? value.filter(item => item !== allValue) : value;

        if (withoutAll.includes(optionId)) {
            const next = withoutAll.filter(item => item !== optionId);
            onChange(next.length ? next : allValue ? [allValue] : []);
            return;
        }

        onChange([...withoutAll, optionId]);
    };

    const removeOption = (optionId: string) => {
        const next = value.filter(item => item !== optionId);
        onChange(next.length ? next : allValue ? [allValue] : []);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    return (
        <div className={className}>
            {label ? (
                <label htmlFor={id} className={labelClassName}>
                    {label}
                </label>
            ) : null}
            <div className="w-full">
                {selectedTags.length > 0 && (
                    <div className={`model-tags-scroll mb-2 flex flex-wrap gap-2 max-h-[110px] overflow-y-auto pr-1 ${classNameContainerScroll}`}>
                        {selectedTags.map(tag => (
                            <span
                                key={tag.id}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xl font-medium bg-gray-100 text-gray-700 border border-gray-300"
                            >
                                {tag.label}
                                <button
                                    type="button"
                                    className="ml-2 inline-flex items-center justify-center text-gray-700 hover:text-gray-900"
                                    onClick={() => removeOption(tag.id)}
                                >
                                    <span aria-hidden="true" className="text-2xl font-bold">×</span>
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <div className={`select dropdown ${dropdownOpen ? 'open' : ''}`} ref={containerRef}>
                    <div className="relative">
                        <div className="input-group cursor-pointer" onClick={() => setDropdownOpen(true)}>
                            {inputAddon ? (
                                <span className="input-group-addon">
                                    <span className={inputAddon} />
                                </span>
                            ) : null}
                            <input
                                type="text"
                                className="form-control"
                                id={id}
                                style={{ paddingRight: 28 }}
                                placeholder={placeholder}
                                value={searchTerm}
                                onFocus={() => setDropdownOpen(true)}
                                onChange={event => {
                                    setSearchTerm(event.target.value);
                                    setDropdownOpen(true);
                                }}
                            />
                        </div>
                        <span
                            className="caret cursor-pointer"
                            style={{ position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)' }}
                            onClick={event => {
                                event.stopPropagation();
                                setDropdownOpen(prev => !prev);
                            }}
                        />
                    </div>

                    <ul className="dropdown-menu" role="menu">
                        {filteredOptions.length === 0 ? (
                            <li className="dropdown-header center">
                                <span className="dropdown-header-text">{emptyMessage}</span>
                            </li>
                        ) : (
                            filteredOptions.map(option => {
                                const optionId = String(option.id);
                                if (option.header) {
                                    return (
                                        <li key={optionId} className="dropdown-header center">
                                            <span className="dropdown-header-text">{option.label}</span>
                                        </li>
                                    );
                                }

                                const isSelected = value.includes(optionId);
                                return (
                                    <li key={optionId} className={isSelected ? 'active' : ''}>
                                        <a
                                            role="menuitem"
                                            className="display-flex align-items-center"
                                            onClick={event => {
                                                event.preventDefault();
                                                toggleOption(optionId);
                                            }}
                                        >
                                            <span>
                                                {option.icon ? <span className="margin-right-5">{option.icon}</span> : null}
                                                {option.label}
                                            </span>
                                        </a>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default TaggedSearchSelect;
